<template lang="pug">
yz-dialog(v-model="showDialog" fullscreen persistent fluid scrollable max-width="1000")
  yz-card
    yz-dialog-fullscreen-header(:title="dialogTitle" :loading="isLoading" :no-actions="readonly" @back="onClose" @cancel="onClose" @save="onSave")
    yz-dialog-fullscreen-content
      div(style="max-width: 850px; margin: 0 auto;")
        //- View mode layout (readonly)
        template(v-if="readonly")
          //- Warehouse details card
          yz-card.mb-4(outlined)
            .pa-4
              .yz-medium-body-sm.neutral700--text.mb-3 Warehouse details
              v-row
                v-col(cols="12" md="4")
                  .yz-regular-body-xs.neutral500--text.mb-1 Name
                  .yz-medium-body-sm.neutral700--text {{ form.name || '-' }}
                v-col(cols="12" md="4")
                  .yz-regular-body-xs.neutral500--text.mb-1 Address
                  .yz-medium-body-sm.neutral700--text {{ formattedAddress || '-' }}
                v-col(cols="12" md="4")
                  .yz-regular-body-xs.neutral500--text.mb-1 Contact details
                  .d-flex.flex-column
                    .yz-medium-body-sm.neutral700--text.mb-1 {{ form.email || '-' }}
                    div
                      yz-btn(v-if="form.phone" underline :label="'+' + form.phone" append-icon="mdi-whatsapp" icon-color="neutral-500" @click="onOpenWhatsApp")

          //- Storage locations card
          yz-card(outlined)
            .pa-4
              .yz-medium-body-sm.neutral700--text.mb-3 Storage locations

              //- Empty state
              yz-empty-state-card(v-if="form.storages.length === 0" icon="$yzi-colors" title="No storage locations" description="No storage locations have been added to this warehouse" no-action)

              //- Storage table
              yz-data-table(v-else :headers="readonlyStorageHeaders" :items="form.storages" header-class="yz-medium-body-xs" body-class="yz-regular-body-sm")
                template(v-slot:item.name="{ item }")
                  span.font-medium {{ item.value }}
                template(v-slot:item.products="{ item }")
                  span {{ item.products ? item.products.length : 0 }}

        //- Edit mode layout
        template(v-else)
          v-form(ref="form" v-model="isFormValid")
            //- Warehouse details card
            yz-card.mb-4
              .pa-4
                .yz-heading-8.text-neutral-700.mb-3 Warehouse details
                .yz-heading-9.text-neutral-700.mb-1 Name
                yz-text-field.mb-5(v-model="form.name" placeholder="Warehouse name" :rules="[rules.required]")

                .yz-heading-9.text-neutral-700.mb-1 Address
                yz-address-input.mb-5(:line1.sync="form.address.address_1" :line2.sync="form.address.address_2" :postcode.sync="form.address.postcode" :city.sync="form.address.city" :state.sync="form.address.state" :country.sync="form.address.country")

                .d-flex.align-start.gap-3
                  .flex-grow-1
                    .yz-heading-9.text-neutral-700.mb-1 Warehouse email
                    yz-text-field(v-model="form.email" placeholder="Email" :rules="[rules.email]")

                  .flex-grow-1
                    .yz-heading-9.text-neutral-700.mb-1 Warehouse phone number
                    yz-phone-input(v-model="form.phone")

            //- Storage locations card
            yz-card
              .pa-4
                .d-flex.align-center.justify-space-between.mb-3
                  .yz-heading-8.text-neutral-700 Storage locations
                  yz-btn(prepend-icon="$yzi-plus" label="Add storage" @click="onAddStorage")

                //- Empty state
                yz-empty-state-card(v-if="form.storages.length === 0" icon="$yzi-colors" title="No items selected" description="Any selected items from your list will show up here" no-action)

                //- Storage table
                yz-data-table(v-else :headers="computedStorageHeaders" :items="form.storages" header-class="yz-medium-body-xs" body-class="yz-regular-body-sm")
                  template(v-slot:item.name="{ item }")
                    span.font-medium {{ item.value }}
                  template(v-slot:item.products="{ item }")
                    span {{ item.products ? item.products.length : 0 }}
                  template(v-slot:item.actions="{ item, index }")
                    .d-flex.align-center
                      yz-btn.mr-2(icon="$yzi-edit-01" outlined @click="onEditStorage(index)")
                      yz-btn(icon="$yzi-trash-03" outlined icon-color="yz-danger-500" @click="onRemoveStorage(index)")

  //- Add Storage Dialog
  add-storage-location-dialog(v-if="showStorageDialog" :open.sync="showStorageDialog" :selected-branch="warehouse" :storage-location="editingStorage" :is-new-warehouse="!isEditMode" @saved="onStorageSaved" @deleted="onStorageDeleted")
  yz-delete-confirmation-dialog(v-model="showDeleteConfirmDialog" item="storage location" description="This action will permanently delete the storage location and remove all items from it. This cannot be undone." :loading="isDeleting" @confirmed="confirmRemoveStorage" @canceled="cancelRemoveStorage")
    yz-card.mt-4.pa-4(v-if="deletingStorage" outlined)
      .yz-medium-body-sm.text-neutral-700 {{ deletingStorage.value }}
      .yz-regular-body-xs.text-neutral-400.mt-2(v-if="deletingStorage.products && deletingStorage.products.length > 0") {{ deletingStorage.products.length }} item(s) will be removed from this location
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddStorageLocationDialog from './AddStorageLocationDialog.vue'

export default {
  components: {
    AddStorageLocationDialog
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    warehouse: {
      type: Object,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      isFormValid: false,
      form: {
        name: '',
        email: '',
        phone: '',
        address: {
          address_1: '',
          address_2: '',
          postcode: '',
          city: '',
          state: '',
          country: 'Malaysia'
        },
        storages: []
      },
      showStorageDialog: false,
      editingStorage: null,
      editingStorageIndex: -1,
      showDeleteConfirmDialog: false,
      deletingStorageIndex: -1,
      isDeleting: false,
      rules: {
        required: v => !!v || 'This field is required',
        email: v => !v || /.+@.+\..+/.test(v) || 'Email must be valid'
      }
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    isEditMode() {
      return !!this.warehouse
    },
    dialogTitle() {
      if (this.readonly) return 'View warehouse details'
      return this.isEditMode ? 'Edit warehouse' : 'Add warehouse'
    },
    formattedAddress() {
      const address = this.form.address
      if (!address) return null
      const parts = [address.address_1, address.address_2, address.postcode, address.city, address.state].filter(Boolean)
      return parts.join(', ')
    },
    readonlyStorageHeaders() {
      return [
        { text: 'Storage name', value: 'name', sortable: false },
        { text: 'Products', value: 'products', sortable: false }
      ]
    },
    computedStorageHeaders() {
      return [
        { text: 'Storage name', value: 'name', sortable: false },
        { text: 'Products', value: 'products', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '100px' }
      ]
    },
    deletingStorage() {
      if (this.deletingStorageIndex < 0) return null
      return this.form.storages[this.deletingStorageIndex]
    }
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.initializeForm()
      }
    }
  },
  mounted() {
    if (this.open) {
      this.initializeForm()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    initializeForm() {
      if (this.warehouse) {
        this.form = {
          name: this.warehouse.name || '',
          email: this.warehouse.email || '',
          phone: this.warehouse.phone || '',
          address: {
            address_1: this.warehouse.address?.address_1 || '',
            address_2: this.warehouse.address?.address_2 || '',
            postcode: this.warehouse.address?.postcode || '',
            city: this.warehouse.address?.city || '',
            state: this.warehouse.address?.state || '',
            country: this.warehouse.address?.country || 'Malaysia'
          },
          storages: (this.warehouse.storageLocations || []).map(s => ({
            id: s.id,
            value: s.name,
            products: s.products || []
          }))
        }
      } else {
        this.resetForm()
      }
    },
    resetForm() {
      this.form = {
        name: '',
        email: '',
        phone: '',
        address: {
          address_1: '',
          address_2: '',
          postcode: '',
          city: '',
          state: '',
          country: 'Malaysia'
        },
        storages: []
      }
      this.isLoading = false
    },
    onClose() {
      this.showDialog = false
    },
    onOpenWhatsApp() {
      if (!this.form.phone) return
      const cleanPhone = this.form.phone.replace(/[^0-9]/g, '')
      window.open(`https://api.whatsapp.com/send?phone=${cleanPhone}&app_absent=1`)
    },
    async onSave() {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', 'Fill in all required fields correctly before saving.'])
        return
      }

      this.isLoading = true

      const address = {
        address_1: this.form.address.address_1 || null,
        postcode: this.form.address.postcode || null,
        city: this.form.address.city || null,
        state: this.form.address.state || null,
        country: this.form.address.country || 'Malaysia'
      }
      if (this.form.address.address_2) {
        address.address_2 = this.form.address.address_2
      }

      const payload = {
        os: 'warehouse',
        name: this.form.name,
        email: this.form.email || null,
        phone: this.form.phone || null,
        address,
        storages: this.form.storages.map(s => ({
          value: s.value,
          products: (s.products || []).map(p => p.id || p) // Extract IDs for API
        }))
      }

      try {
        if (this.isEditMode) {
          await this.$axios.patch(`/companies/${this.company.id}/branches/${this.warehouse.id}`, payload)
          this.showMessage(['success', 'Warehouse updated successfully'])
        } else {
          await this.$axios.post(`/companies/${this.company.id}/branches`, payload)
          this.showMessage(['success', 'Warehouse created successfully'])
        }
        this.$emit('saved')
        this.showDialog = false
      } catch (error) {
        console.error('Error saving warehouse:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to save warehouse. Please try again.'
        this.showMessage(['error', errorMessage])
      } finally {
        this.isLoading = false
      }
    },
    onAddStorage() {
      this.editingStorage = null
      this.editingStorageIndex = -1
      this.showStorageDialog = true
    },
    onEditStorage(index) {
      const storage = this.form.storages[index]
      this.editingStorage = {
        id: storage.id,
        value: storage.value,
        products: storage.products || []
      }
      this.editingStorageIndex = index
      this.showStorageDialog = true
    },
    onStorageSaved(storage) {
      const storageData = {
        value: storage.name,
        products: storage.items // Keep full item objects for re-editing
      }
      if (this.editingStorageIndex >= 0) {
        if (storage.id) {
          storageData.id = storage.id
        }
        this.$set(this.form.storages, this.editingStorageIndex, storageData)
      } else {
        this.form.storages.push(storageData)
      }
      this.editingStorage = null
      this.editingStorageIndex = -1
      // Emit refresh for parent when in edit mode (API calls were made)
      if (this.isEditMode) {
        this.$emit('storage-changed')
      }
    },
    onStorageDeleted(storageId) {
      // Remove from local state
      const index = this.form.storages.findIndex(s => s.id === storageId)
      if (index > -1) {
        this.form.storages.splice(index, 1)
      }
      this.editingStorage = null
      this.editingStorageIndex = -1
      // Emit refresh for parent (API delete was made)
      if (this.isEditMode) {
        this.$emit('storage-changed')
      }
    },
    onRemoveStorage(index) {
      this.deletingStorageIndex = index
      this.showDeleteConfirmDialog = true
    },
    async confirmRemoveStorage() {
      if (this.deletingStorageIndex < 0) return

      const storage = this.form.storages[this.deletingStorageIndex]

      // If edit mode and storage has an ID, call API to delete
      if (this.isEditMode && storage?.id) {
        this.isDeleting = true
        try {
          await this.$axios.delete(`/clinics/${this.warehouse.slug}/inventories/locations/${storage.id}`)
          this.showMessage(['success', 'Storage location deleted'])
          this.form.storages.splice(this.deletingStorageIndex, 1)
          this.$emit('storage-changed')
        } catch (error) {
          console.error('Error deleting storage location:', error)
          const errorMessage = error.response?.data?.detail || 'Failed to delete storage location'
          this.showMessage(['error', errorMessage])
        } finally {
          this.isDeleting = false
        }
      } else {
        // Create mode or storage without ID - just splice the array
        this.form.storages.splice(this.deletingStorageIndex, 1)
      }

      this.deletingStorageIndex = -1
      this.showDeleteConfirmDialog = false
    },
    cancelRemoveStorage() {
      this.deletingStorageIndex = -1
      this.showDeleteConfirmDialog = false
    }
  }
}
</script>

<style lang="scss" scoped></style>
